import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Eye, Calendar, DollarSign, TrendingUp, MapPin, Users, Award, Filter, Search, X, ExternalLink, Play } from 'lucide-react';

const projects = [
  {
    title: "Thwake Dam",
    category: "Water Management",
    description: "Major water infrastructure project involving dam construction and water management systems.",
    completion: "87%",
    amount: "KES 1.89B",
    image: "https://img.freepik.com/free-photo/beautiful-shot-pier-with-cloudy-gray-sky_181624-13571.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "24 months",
    location: "Makueni County",
    team: "45 Engineers",
    client: "Ministry of Water",
    startDate: "2022-03-15",
    endDate: "2024-12-30",
    features: [
      "Dam Construction",
      "Water Management Systems",
      "Environmental Impact Assessment",
      "Community Development"
    ],
    keyStats: {
      capacity: "688 Million m³",
      height: "80 meters",
      beneficiaries: "3 Million people"
    }
  },
  {
    title: "Konza City Development",
    category: "Urban Development",
    description: "Smart city development project with modern infrastructure and sustainable design.",
    completion: "28%",
    amount: "KES 549.6M",
    image: "https://img.freepik.com/free-photo/beautiful-shot-richmond-virginia-skyline-with-cloudy-blue-sky_181624-18968.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "20 months",
    location: "Machakos County",
    team: "30 Specialists",
    client: "KonzaTechnopolis",
    startDate: "2023-06-01",
    endDate: "2025-02-28",
    features: [
      "Smart Infrastructure",
      "Sustainable Design",
      "Digital Integration",
      "Green Spaces"
    ],
    keyStats: {
      area: "5,000 acres",
      buildings: "50+ structures",
      jobs: "20,000 expected"
    }
  },
  {
    title: "Thwake Dam Reinforcement",
    category: "Water Management",
    description: "Specialized reinforcement work for the Thwake Dam project.",
    completion: "0%",
    amount: "KES 36M",
    image: "https://img.freepik.com/free-photo/wide-angle-shot-part-landschaftspark-duisburg-germany-daytime_181624-10664.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "Pending",
    timeline: "36 months",
    location: "Makueni County",
    team: "25 Engineers",
    client: "Ministry of Water",
    startDate: "2024-01-15",
    endDate: "2027-01-15",
    features: [
      "Rebar Installation",
      "Quality Control",
      "Safety Protocols",
      "Technical Expertise"
    ],
    keyStats: {
      steel: "2,500 tons",
      concrete: "15,000 m³",
      workforce: "200 workers"
    }
  },
  {
    title: "Nairobi Expressway",
    category: "Road Construction",
    description: "Major highway construction project connecting key areas of Nairobi.",
    completion: "95%",
    amount: "KES 1.2B",
    image: "https://img.freepik.com/free-photo/urban-traffic-with-cityscape_1359-1091.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "Near Completion",
    timeline: "18 months",
    location: "Nairobi County",
    team: "60 Engineers",
    client: "Kenya Urban Roads Authority",
    startDate: "2022-01-10",
    endDate: "2023-07-10",
    features: [
      "Highway Construction",
      "Traffic Management",
      "Safety Systems",
      "Environmental Compliance"
    ],
    keyStats: {
      length: "27 kilometers",
      lanes: "4 lanes dual",
      bridges: "12 overpasses"
    }
  },
  {
    title: "Mombasa Port Expansion",
    category: "Infrastructure",
    description: "Port expansion project to increase capacity and efficiency.",
    completion: "45%",
    amount: "KES 750M",
    image: "https://img.freepik.com/free-photo/petroleum-gas-container-ship-oil-refinery-background-energy-nautical-transportation_1127-3250.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "30 months",
    location: "Mombasa County",
    team: "40 Specialists",
    client: "Kenya Ports Authority",
    startDate: "2023-03-01",
    endDate: "2025-09-01",
    features: [
      "Port Expansion",
      "Equipment Installation",
      "Logistics Systems",
      "Security Infrastructure"
    ],
    keyStats: {
      capacity: "+40% increase",
      berths: "3 new berths",
      cranes: "8 gantry cranes"
    }
  },
  {
    title: "Industrial Park",
    category: "Industrial Construction",
    description: "Development of a modern industrial park with state-of-the-art facilities.",
    completion: "75%",
    amount: "KES 4.2B",
    image: "https://img.freepik.com/free-photo/park-lujiazui-financial-centre_1127-3344.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "2021-2024",
    location: "Kiambu County",
    team: "80 Engineers",
    client: "Private Consortium",
    startDate: "2021-01-01",
    endDate: "2024-12-31",
    features: ["Manufacturing Facilities", "Logistics Hub", "Energy Efficient Design", "Waste Management"],
    keyStats: {
      area: "1,200 acres",
      factories: "25 facilities",
      employment: "5,000 jobs"
    }
  },
  {
    title: "Modern Hospital Complex",
    category: "Healthcare Infrastructure",
    description: "State-of-the-art medical facility with advanced healthcare technology.",
    completion: "95%",
    amount: "KES 1.8B",
    image: "https://img.freepik.com/free-photo/clean-empty-hospital-ward-ready-receive-patients-reflecting-modern-medical-care_91128-4460.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "Near Completion",
    timeline: "2022-2024",
    location: "Nairobi County",
    team: "35 Specialists",
    client: "Ministry of Health",
    startDate: "2022-01-01",
    endDate: "2024-12-31",
    features: ["Emergency Center", "Surgical Suites", "Diagnostic Labs", "Patient Care Units"],
    keyStats: {
      beds: "500 bed capacity",
      floors: "12 floors",
      departments: "15 specialized"
    }
  },
  {
    title: "Green Energy Plant",
    category: "Renewable Energy",
    description: "Solar and wind hybrid power generation facility for sustainable energy.",
    completion: "40%",
    amount: "KES 3.2B",
    image: "https://img.freepik.com/free-vector/renewable-energy-sources-flat-background_1284-71573.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "2023-2026",
    location: "Turkana County",
    team: "50 Engineers",
    client: "Kenya Electricity Board",
    startDate: "2023-01-01",
    endDate: "2026-12-31",
    features: ["Solar Arrays", "Wind Turbines", "Energy Storage", "Grid Integration"],
    keyStats: {
      capacity: "310 MW",
      turbines: "125 wind turbines",
      panels: "890,000 solar panels"
    }
  },
  {
    title: "Thwake Dam Tunnel Excavation",
    category: "Water Management",
    description: "Excavation and supporting of approach and discharge tunnels at Thwake Dam for Russell & Clarke (E.A) Limited.",
    completion: "65%",
    amount: "USD 2.83M",
    image: "https://img.freepik.com/free-photo/salina-turda-salt-mine-romania_1268-20071.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740",
    status: "In Progress",
    timeline: "18 months",
    location: "Makueni County",
    team: "35 Engineers",
    client: "Russell & Clarke (E.A) Limited",
    startDate: "2023-03-01",
    endDate: "2024-09-01",
    features: [
      "Tunnel Excavation",
      "Structural Support Systems",
      "Safety Engineering",
      "Technical Supervision"
    ],
    keyStats: {
      length: "2.5 kilometers",
      diameter: "4.2 meters",
      depth: "35 meters"
    }
  }
];

const categories = ["All", "Water Management", "Urban Development", "Road Construction", "Infrastructure", "Industrial Construction", "Healthcare Infrastructure", "Renewable Energy"];

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("completion");
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const observerRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    return () => observerRef.current?.disconnect();
  }, []);

  const filteredProjects = projects
    .filter(project => {
      const matchesCategory = activeCategory === "All" || project.category === activeCategory;
      const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "completion":
          return parseInt(b.completion) - parseInt(a.completion);
        case "amount":
          return parseFloat(b.amount.replace(/[^\d.]/g, '')) - parseFloat(a.amount.replace(/[^\d.]/g, ''));
        case "title":
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    switch(status) {
      case "Completed": return "from-green-500 to-emerald-600";
      case "Near Completion": return "from-blue-500 to-cyan-600";
      case "In Progress": return "from-amber-500 to-orange-600";
      default: return "from-slate-500 to-gray-600";
    }
  };

  const getCompletionColor = (completion) => {
    const percent = parseInt(completion);
    if (percent >= 90) return "from-green-500 to-emerald-600";
    if (percent >= 70) return "from-blue-500 to-cyan-600";
    if (percent >= 50) return "from-amber-500 to-orange-600";
    return "from-red-500 to-pink-600";
  };

  const ProjectCard = ({ project, index, isListView = false }) => {
    const cardRef = useRef();

    useEffect(() => {
      if (cardRef.current && observerRef.current) {
        observerRef.current.observe(cardRef.current);
      }
    }, []);

    if (isListView) {
      return (
        <div
          ref={cardRef}
          className="opacity-0 group relative animate-on-scroll bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-blue-500/30 transition-all duration-500 hover:bg-white/[0.04]"
          style={{ animationDelay: `${index * 0.1}s` }}
          onClick={() => setSelectedProject(project)}
        >
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-64 flex-shrink-0">
              <div className="relative h-40 lg:h-32 rounded-xl overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute top-2 right-2">
                  <div className={`px-2 py-1 bg-gradient-to-r ${getStatusColor(project.status)} text-white text-xs font-semibold rounded-full`}>
                    {project.status}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-1 rounded-full">
                      {project.category}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {project.location}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-slate-300 text-sm mb-3 line-clamp-2">
                    {project.description}
                  </p>
                </div>
                
                <div className="text-right">
                  <div className="text-2xl font-bold text-white mb-1">{project.completion}</div>
                  <div className="text-sm text-slate-400">Complete</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-slate-800/20 rounded-lg">
                  <DollarSign className="w-4 h-4 text-green-400 mx-auto mb-1" />
                  <div className="text-sm font-semibold text-white">{project.amount}</div>
                  <div className="text-xs text-slate-400">Budget</div>
                </div>
                <div className="text-center p-3 bg-slate-800/20 rounded-lg">
                  <Calendar className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                  <div className="text-sm font-semibold text-white">{project.timeline}</div>
                  <div className="text-xs text-slate-400">Timeline</div>
                </div>
                <div className="text-center p-3 bg-slate-800/20 rounded-lg">
                  <Users className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                  <div className="text-sm font-semibold text-white">{project.team}</div>
                  <div className="text-xs text-slate-400">Team</div>
                </div>
                <div className="text-center p-3 bg-slate-800/20 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-orange-400 mx-auto mb-1" />
                  <div className="text-sm font-semibold text-white">{project.completion}</div>
                  <div className="text-xs text-slate-400">Progress</div>
                </div>
              </div>
              
              <div className="w-full bg-slate-700/50 rounded-full h-2">
                <div 
                  className={`h-2 bg-gradient-to-r ${getCompletionColor(project.completion)} rounded-full transition-all duration-1000`}
                  style={{ width: project.completion }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={cardRef}
        className="opacity-0 group relative animate-on-scroll cursor-pointer"
        style={{ animationDelay: `${index * 0.1}s` }}
        onClick={() => setSelectedProject(project)}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
        <div className="relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-500 hover:bg-white/[0.04] h-full">
          
          {/* Project Image */}
          <div className="relative h-48 lg:h-56 overflow-hidden">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            
            {/* Status Badge */}
            <div className="absolute top-4 right-4">
              <div className={`px-3 py-1 bg-gradient-to-r ${getStatusColor(project.status)} text-white text-xs font-semibold rounded-full shadow-lg backdrop-blur-sm`}>
                {project.status}
              </div>
            </div>

            {/* Completion Badge */}
            <div className="absolute top-4 left-4">
              <div className="px-3 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold rounded-full border border-white/20">
                {project.completion} Complete
              </div>
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-purple-600/90 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center backdrop-blur-sm">
              <div className="text-center text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                <Eye className="w-8 h-8 mx-auto mb-2" />
                <span className="text-sm font-medium">View Details</span>
              </div>
            </div>
          </div>

          {/* Project Content */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-1 rounded-full">
                {project.category}
              </span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {project.location}
              </span>
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300 line-clamp-1">
              {project.title}
            </h3>
            
            <p className="text-slate-300 text-sm mb-4 line-clamp-3">
              {project.description}
            </p>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-slate-400">Progress</span>
                <span className="text-xs font-semibold text-white">{project.completion}</span>
              </div>
              <div className="w-full bg-slate-700/50 rounded-full h-2">
                <div 
                  className={`h-2 bg-gradient-to-r ${getCompletionColor(project.completion)} rounded-full transition-all duration-1000`}
                  style={{ width: project.completion }}
                />
              </div>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-green-400" />
                <div>
                  <span className="text-slate-400">Budget</span>
                  <div className="text-white font-semibold">{project.amount}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                <div>
                  <span className="text-slate-400">Timeline</span>
                  <div className="text-white font-semibold">{project.timeline}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <section className="relative min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading Portfolio...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="relative min-h-screen bg-slate-950 overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-gradient-to-br from-cyan-500/15 to-blue-500/15 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,transparent_0%,rgba(15,23,42,0.4)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 backdrop-blur-sm border border-blue-500/20 rounded-full px-4 py-2 text-sm font-medium text-blue-400 mb-6">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            Our Work Portfolio
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Landmark Projects
            <span className="block bg-gradient-to-r from-blue-400 via-cyan-500 to-purple-600 bg-clip-text text-transparent">
              Shaping Kenya's Future
            </span>
          </h2>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of transformative construction and engineering projects 
            that demonstrate our commitment to excellence and innovation.
          </p>
        </div>

        {/* Controls Section */}
        <div className="mb-8 lg:mb-12">
          {/* Search and View Toggle */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
              >
                <option value="completion">Sort by Progress</option>
                <option value="amount">Sort by Budget</option>
                <option value="title">Sort by Name</option>
              </select>
              
              <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-xl p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-2 rounded-lg transition-all duration-200 ${
                    viewMode === "grid" ? "bg-blue-500 text-white" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-2 rounded-lg transition-all duration-200 ${
                    viewMode === "list" ? "bg-blue-500 text-white" : "text-slate-400 hover:text-white"
                  }`}
                >
                  <div className="w-4 h-4 flex flex-col gap-0.5">
                    <div className="bg-current h-0.5 rounded-full"></div>
                    <div className="bg-current h-0.5 rounded-full"></div>
                    <div className="bg-current h-0.5 rounded-full"></div>
                    <div className="bg-current h-0.5 rounded-full"></div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 lg:gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 lg:px-6 lg:py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                    : 'bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:border-blue-500/30 hover:text-blue-400'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Projects Display */}
        <div className="mb-12 lg:mb-16">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No Projects Found</h3>
              <p className="text-slate-400">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-slate-400">
                  Showing {filteredProjects.length} of {projects.length} projects
                </p>
                <div className="text-sm text-slate-400">
                  View: <span className="text-white capitalize">{viewMode}</span>
                </div>
              </div>

              {/* Projects Grid/List */}
              <div className={viewMode === "grid" 
                ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8" 
                : "space-y-6"
              }>
                {filteredProjects.map((project, index) => (
                  <ProjectCard 
                    key={project.title} 
                    project={project} 
                    index={index} 
                    isListView={viewMode === "list"}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-slate-900/40 to-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 lg:p-12 max-w-4xl mx-auto">
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Ready to Start Your Project?</h3>
            <p className="text-slate-300 mb-8 text-lg leading-relaxed max-w-2xl mx-auto">
              Let's discuss how we can bring your vision to life with our expertise and innovation. 
              From concept to completion, we're your trusted partner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center justify-center gap-2">
                  Get In Touch
                  <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
              <button className="px-8 py-4 border border-slate-600 text-slate-300 font-semibold rounded-xl hover:border-blue-500/50 hover:text-blue-400 transition-all duration-300 flex items-center justify-center gap-2">
                <Play className="w-5 h-5" />
                Watch Our Story
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-3xl overflow-hidden">
              
              {/* Modal Header */}
              <div className="relative h-64 sm:h-80 lg:h-96">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                
                {/* Close Button */}
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-12 h-12 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-all duration-300 border border-white/20"
                >
                  <X className="w-6 h-6" />
                </button>

                {/* Project Title Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-sm font-medium text-blue-400 bg-blue-500/20 backdrop-blur-sm px-3 py-1 rounded-full border border-blue-500/30">
                      {selectedProject.category}
                    </span>
                    <span className={`text-sm font-medium text-white bg-gradient-to-r ${getStatusColor(selectedProject.status)} px-3 py-1 rounded-full`}>
                      {selectedProject.status}
                    </span>
                    <span className="text-sm text-slate-300 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full border border-white/20 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {selectedProject.location}
                    </span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
                    {selectedProject.title}
                  </h3>
                  <p className="text-lg text-slate-200 max-w-3xl">
                    {selectedProject.description}
                  </p>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 lg:p-8">
                {/* Tab Navigation */}
                <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-700/50">
                  {["overview", "features", "progress", "team"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-300 capitalize ${
                        activeTab === tab
                          ? "text-blue-400 border-b-2 border-blue-400 bg-blue-500/5"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                {activeTab === "overview" && (
                  <div className="space-y-8">
                    {/* Project Stats Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                      <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-4 lg:p-6 text-center">
                        <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                        <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent mb-1">
                          {selectedProject.completion}
                        </div>
                        <div className="text-sm text-slate-400 uppercase tracking-wider">Completion</div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4 lg:p-6 text-center">
                        <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
                        <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent mb-1">
                          {selectedProject.amount}
                        </div>
                        <div className="text-sm text-slate-400 uppercase tracking-wider">Project Value</div>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 lg:p-6 text-center">
                        <Calendar className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-1">
                          {selectedProject.timeline}
                        </div>
                        <div className="text-sm text-slate-400 uppercase tracking-wider">Timeline</div>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-xl p-4 lg:p-6 text-center">
                        <Users className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                        <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-orange-400 to-yellow-500 bg-clip-text text-transparent mb-1">
                          {selectedProject.team}
                        </div>
                        <div className="text-sm text-slate-400 uppercase tracking-wider">Team Size</div>
                      </div>
                    </div>

                    {/* Key Statistics */}
                    <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 lg:p-8">
                      <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <Award className="w-6 h-6 text-blue-400" />
                        Key Statistics
                      </h4>
                      <div className="grid md:grid-cols-3 gap-6">
                        {Object.entries(selectedProject.keyStats).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-2xl font-bold text-white mb-1">{value}</div>
                            <div className="text-sm text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "features" && (
                  <div className="space-y-6">
                    <h4 className="text-xl font-bold text-white mb-6">Key Features & Capabilities</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {selectedProject.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-4 bg-slate-800/20 border border-slate-700/30 rounded-lg p-4 hover:bg-slate-700/20 transition-all duration-300">
                          <div className="w-3 h-3 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full flex-shrink-0" />
                          <span className="text-slate-300 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === "progress" && (
                  <div className="space-y-6">
                    <h4 className="text-xl font-bold text-white mb-6">Project Progress</h4>
                    <div className="bg-slate-800/20 border border-slate-700/30 rounded-xl p-6 lg:p-8">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg text-slate-300">Overall Completion</span>
                        <span className="text-3xl font-bold text-white">{selectedProject.completion}</span>
                      </div>
                      <div className="w-full bg-slate-700/50 rounded-full h-4 mb-6">
                        <div 
                          className={`h-4 bg-gradient-to-r ${getCompletionColor(selectedProject.completion)} rounded-full transition-all duration-1000 relative overflow-hidden`}
                          style={{ width: selectedProject.completion }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-pulse" />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="text-sm text-slate-400 mb-1">Start Date</div>
                          <div className="text-white font-semibold">{new Date(selectedProject.startDate).toLocaleDateString()}</div>
                        </div>
                        <div>
                          <div className="text-sm text-slate-400 mb-1">Expected Completion</div>
                          <div className="text-white font-semibold">{new Date(selectedProject.endDate).toLocaleDateString()}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "team" && (
                  <div className="space-y-6">
                    <h4 className="text-xl font-bold text-white mb-6">Project Information</h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-slate-800/20 border border-slate-700/30 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Users className="w-6 h-6 text-blue-400" />
                          <h5 className="text-lg font-semibold text-white">Team</h5>
                        </div>
                        <div className="text-2xl font-bold text-blue-400 mb-1">{selectedProject.team}</div>
                        <div className="text-sm text-slate-400">Working on this project</div>
                      </div>
                      <div className="bg-slate-800/20 border border-slate-700/30 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Award className="w-6 h-6 text-green-400" />
                          <h5 className="text-lg font-semibold text-white">Client</h5>
                        </div>
                        <div className="text-lg font-bold text-green-400 mb-1">{selectedProject.client}</div>
                        <div className="text-sm text-slate-400">Project stakeholder</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-5deg); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite 2s;
        }
        .animate-on-scroll {
          animation: fade-in 0.8s ease-out forwards;
        }
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.5);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.7);
        }

        /* Mobile optimizations */
        @media (max-width: 640px) {
          .animate-on-scroll {
            animation-delay: 0s !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Portfolio;